/**
 * Central map of server error codes → applicant-safe messages, read by
 * {@link classifySaveError} (./steps.ts) and by ReferenceDetailsStep, so
 * every caller that classifies a server rejection uses the same wording for
 * the same code instead of each component inventing its own copy or
 * regex-matching `.message` (the bug class both TermsGateModal and
 * ReferenceDetailsStep used to have — see their own history/commits).
 *
 * Local codes are mirrored from server/src/common/error-codes.ts (codes
 * this repo's BFF constructs itself — steps.test.ts asserts
 * the two lists agree). Everything else is an upstream Globus Hub code
 * (Agreements Engine, Reference Management) forwarded verbatim through
 * GlobusService.rethrow(); this repo doesn't define them, but the client
 * still needs to know how to talk about them.
 */
export const ERROR_CODE_MESSAGES = {
  STEP_LOCKED: 'This section has been locked. Please refresh the page to see the latest status.',
  PASSPORT_FINAL_SAVE_REQUIRED:
    'Save and submit your passport step to apply your passport details. Extraction alone cannot update your profile.',
  PASSPORT_PROVENANCE_UNAVAILABLE:
    'Passport saving is temporarily unavailable. Your entries are still on this page. Please try again when the service is available.',
  AGREEMENT_VERSION_STALE:
    'This agreement was updated while you were signing it — please refresh the page and sign the latest version.',
  AGREEMENT_NOT_APPLICABLE:
    'This agreement no longer applies to your application — please refresh the page to continue.',
  TYPED_NAME_REQUIRED: 'Please type your full legal name exactly as shown to sign this agreement.',
  DUPLICATE_REFEREE: 'You already have an active reference request for this email address.',
  MAX_REFERENCES: 'You already have the maximum number of active references.',
  REVOKE_CONFLICT:
    "This reference just changed state (your referee may have submitted). Refresh the page to see the latest.",
  COOLDOWN: 'A reminder was sent recently — you can resend again after 24 hours.',
  LIMIT: 'The maximum number of reminders has been sent. You can replace the referee instead.',
} as const;

export type KnownErrorCode = keyof typeof ERROR_CODE_MESSAGES;
